export interface RollDetail {
	Id: number;
	Name: string;
	MillSizeName: string | null;
	StatusName: string | null;
	BlankDiameter: number | null;
	NewDiameter: number | null;
	Diameter: number | null;
	ScrapDiameter: number | null;
	ValuePer1000th: number | null;
	CurrentPercentageLeft: number | null;
	RollApplicationName: string | null;
	RollMaterialName: string | null;
	Hardness: number | null;
	ShellDepth: number | null;
	BarrelAssignedName: string | null;
	Cost: number | null;
	CurrentCost: number | null;
	Comments: string | null;
	PurchaseOrderName: string | null;
	PurchaseOrderItemNumber: number | null;
	SupplierName: string | null;
	SupplierInvoiceNumber: string | null;
	CostCenterName: string | null;
	// Raw columns, never converted. The *String twins exist too, but the legacy
	// screen showed them shifted a day.
	OrderTimestamp: string | null;
	ReceiveTimestamp: string | null;
	FirstTurnedTimestamp: string | null;
	LastTurnedTimestamp: string | null;
	RemoveReasonName: string | null;
	RemoveTimestampString: string | null;
	RollsetAssignedName: string | null;
	RollsetAssignedPosition: string | null;
	C: number | null;
	Mn: number | null;
	P: number | null;
	S: number | null;
	Si: number | null;
	Cr: number | null;
	Ni: number | null;
	Mo: number | null;
	Mg: number | null;
	Co: number | null;
	Va: number | null;
	Cu: number | null;
	Al: number | null;
	V: number | null;
	W: number | null;
}

export interface RollTurn {
	Id: number;
	TimestampString: string | null;
	Diameter: number | null;
	DiameterTurnedOff: number | null;
	TimeToCut: number | null;
	Operator1Name: string | null;
	Operator2Name: string | null;
	Comments: string | null;
}

export interface RollNameOption {
	Name: string;
}

export const CHEMISTRY_ELEMENTS = [
	"C",
	"Mn",
	"P",
	"S",
	"Si",
	"Cr",
	"Ni",
	"Mo",
	"Mg",
	"Co",
	"Va",
	"Cu",
	"Al",
	"V",
	"W",
] as const;

export type ChemistryElement = (typeof CHEMISTRY_ELEMENTS)[number];

export const EMPTY = "-";

/**
 * Takes the day off a value that already starts with `YYYY-MM-DD`, whether it arrives as a
 * raw datetime (`2021-05-19T00:00:00`) or as a string the procedure formatted. It cuts
 * rather than parses on purpose: the purchase and turning dates are calendar dates, not
 * instants, and parsing them would let a browser in another timezone move the day.
 */
export const formatCalendarDate = (value?: string | null): string => {
	if (!value) return EMPTY;
	const [year, month, day] = String(value).slice(0, 10).split("-");
	return year && month && day ? `${month}/${day}/${year}` : EMPTY;
};

export const formatDecimals = (
	value: number | null | undefined,
	decimals: number,
): string => (value == null ? EMPTY : Number(value).toFixed(decimals));

export const formatMoney = (value: number | null | undefined): string =>
	value == null ? EMPTY : `$ ${Number(value).toFixed(2)}`;

export const formatText = (
	value: string | number | null | undefined,
): string => {
	if (value == null) return EMPTY;
	const text = String(value).trim();
	return text.length > 0 ? text : EMPTY;
};

export interface DetailField {
	label: string;
	value: (roll: RollDetail) => string;
	help: string;
}

export interface DetailSection {
	title: string;
	fields: DetailField[];
}

export const ALL_SECTIONS: DetailSection[] = [
	{
		title: "Identity",
		fields: [
			{
				label: "Mill Size",
				value: (r) => formatText(r.MillSizeName),
				help: "Product family the roll is ground for",
			},
			{
				label: "Roll Status",
				value: (r) => formatText(r.StatusName),
				help: "Free, assigned to a rollset, broken or scrapped",
			},
			{
				label: "Roll Application",
				value: (r) => formatText(r.RollApplicationName),
				help: "Stand and pass shape the roll is cut for",
			},
			{
				label: "Roll Material",
				value: (r) => formatText(r.RollMaterialName),
				help: "Cast iron grade of the roll",
			},
			{
				label: "Roll Hardness",
				value: (r) => formatText(r.Hardness),
				help: "Shore hardness measured on receipt",
			},
		],
	},
	{
		title: "Geometry",
		fields: [
			{
				label: "Roll Blank Diameter",
				value: (r) => formatDecimals(r.BlankDiameter, 3),
				help: "Diameter of the raw casting, in inches",
			},
			{
				label: "Roll New Diameter",
				value: (r) => formatDecimals(r.NewDiameter, 3),
				help: "Diameter after the first cut, where its usable life starts, in inches",
			},
			{
				label: "Roll Current Diameter",
				value: (r) => formatDecimals(r.Diameter, 3),
				help: "Diameter after the last turn, in inches",
			},
			{
				label: "Roll Scrap Diameter",
				value: (r) => formatDecimals(r.ScrapDiameter, 3),
				help: "Diameter at which the roll is discarded, in inches",
			},
			{
				label: "Roll Shell Depth",
				value: (r) => formatDecimals(r.ShellDepth, 3),
				help: "Depth of the hard outer shell, in inches",
			},
		],
	},
	{
		title: "Value",
		fields: [
			{
				label: "Roll Cost",
				value: (r) => formatMoney(r.Cost),
				help: "What the roll was bought for",
			},
			{
				label: "Roll Current Value",
				value: (r) => formatMoney(r.CurrentCost),
				help: "Cost prorated over the diameter still left to use",
			},
			{
				label: "Value per 1000th Inch",
				value: (r) => formatDecimals(r.ValuePer1000th, 6),
				help: "What one thousandth of an inch of this roll costs",
			},
		],
	},
	{
		title: "Purchase",
		fields: [
			{
				label: "Purchase Order",
				value: (r) => formatText(r.PurchaseOrderName),
				help: "Order the roll was bought on",
			},
			{
				label: "PO Item Number",
				value: (r) => formatText(r.PurchaseOrderItemNumber),
				help: "Line of that order",
			},
			{
				label: "Supplier Name",
				value: (r) => formatText(r.SupplierName),
				help: "Foundry that cast the roll",
			},
			{
				label: "Supplier Invoice",
				value: (r) => formatText(r.SupplierInvoiceNumber),
				help: "Invoice the roll arrived on",
			},
			{
				label: "Cost Center",
				value: (r) => formatText(r.CostCenterName),
				help: "Account the roll is charged to",
			},
		],
	},
	{
		title: "Dates",
		fields: [
			{
				label: "Date Roll Ordered",
				value: (r) => formatCalendarDate(r.OrderTimestamp),
				help: "Day the purchase order was placed",
			},
			{
				label: "Date Roll Received",
				value: (r) => formatCalendarDate(r.ReceiveTimestamp),
				help: "Day the roll arrived at the shop",
			},
			{
				label: "Date Roll First Turned",
				value: (r) => formatCalendarDate(r.FirstTurnedTimestamp),
				help: "Day of the first cut. Kept on the roll, not derived from the turn history",
			},
			{
				label: "Date Roll Last Turned",
				value: (r) => formatCalendarDate(r.LastTurnedTimestamp),
				help: "Day of the last cut. Kept on the roll, so it can disagree with the newest row below",
			},
		],
	},
	{
		title: "Assignment",
		fields: [
			{
				label: "Assigned Set",
				value: (r) => formatText(r.RollsetAssignedName),
				help: "Rollset the roll is mounted on",
			},
			{
				label: "Assigned Position",
				value: (r) => formatText(r.RollsetAssignedPosition),
				help: "Where it sits in that rollset, top or bottom",
			},
			{
				label: "Roll Barrel Description",
				value: (r) => formatText(r.BarrelAssignedName),
				help: "Barrel that rollset drops into",
			},
			{
				label: "Remove Reason",
				value: (r) => formatText(r.RemoveReasonName),
				help: "Why the roll was last taken off the line",
			},
			{
				label: "Date Removed",
				// Unlike the four dates above, this one is a real instant the procedure
				// already converted to plant time, so only its day is taken.
				value: (r) => formatCalendarDate(r.RemoveTimestampString),
				help: "Day the roll was taken out of its last rollset, empty while it is still in it",
			},
		],
	},
];

export const TURN_COLUMN_HELP: Record<string, string> = {
	TimestampString: "Day the roll was turned on the lathe",
	Diameter: "Diameter the roll was left at, in inches",
	DiameterTurnedOff:
		"Diameter removed on this turn, in inches. Empty on the first recorded cut",
	TimeToCut: "How long the turn took, in minutes",
	Operator1Name: "First operator on the lathe",
	Operator2Name: "Second operator on the lathe",
	Comments: "Notes the operator left, cut and depth per pass",
};
